import { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { CustomModal, CustomModalProps, StyledTextArea } from 'components/shared';
import { useModal } from 'hooks/useModal';
import { CommentDTO } from 'interfaces';
import { HTTPService } from 'service';
import { logNetworkError } from 'utils/logNetworkError';

import styles from './ClientDetailsCommentModal.module.css';

interface ClientDetailsCommentModalProps extends CustomModalProps {
   commentId: string;
   comment: string;
   action: 'ADD' | 'EDIT' | 'DELETE';
   clientId: string;
   setComments: React.Dispatch<React.SetStateAction<CommentDTO[]>>;
   setItemsTotalCount: React.Dispatch<React.SetStateAction<number>>;
}

const ClientDetailsCommentModal = ({
   commentId,
   comment,
   action,
   clientId,
   setComments,
   setItemsTotalCount,
   onClose,
   ...props
}: ClientDetailsCommentModalProps) => {
   const [commentText, setCommentText] = useState(comment);
   const { t } = useTranslation();

   const handleClose = useCallback(() => {
      setCommentText('');
      onClose();
   }, [onClose]);

   const {
      isLoading,
      errorInfo,
      setIsLoading,
      setErrorInfo,
      showCloseWarning,
      handleShowCloseWarning,
      handleHideCloseWarning,
      closeModalHandler,
   } = useModal({ onClose: handleClose });

   const postComment = useCallback(() => {
      setIsLoading(true);
      HTTPService.postUserComment(clientId, commentText)
         .then(res => {
            setComments(prevValues => [...prevValues, res.data]);
            setItemsTotalCount(prevValue => prevValue + 1);
            closeModalHandler();
         })
         .catch(err => {
            logNetworkError(err);
            setErrorInfo(t('modals.comment.error_add'));
         })
         .finally(() => setIsLoading(false));
   }, [
      commentText,
      clientId,
      setIsLoading,
      setComments,
      setItemsTotalCount,
      setErrorInfo,
      closeModalHandler,
      t,
   ]);

   const editComment = useCallback(() => {
      setIsLoading(true);
      HTTPService.editUserComment(clientId, commentId!, commentText)
         .then(res => {
            setComments(prevValues =>
               prevValues.map(item => (item.id === commentId ? res.data : item)),
            );
            closeModalHandler();
         })
         .catch(err => {
            logNetworkError(err);
            setErrorInfo(t('modals.comment.error_edit'));
         })
         .finally(() => setIsLoading(false));
   }, [
      commentText,
      clientId,
      commentId,
      closeModalHandler,
      setComments,
      setIsLoading,
      setErrorInfo,
      t,
   ]);

   const deleteComment = useCallback(() => {
      setIsLoading(true);
      HTTPService.deleteUserComment(clientId, commentId!)
         .then(() => {
            setComments(prevValues => prevValues.filter(item => item.id !== commentId));
            setItemsTotalCount(prevValue => prevValue - 1);
            closeModalHandler();
         })
         .catch(err => {
            logNetworkError(err);
            setErrorInfo(t('modals.comment.error_delete'));
         })
         .finally(() => setIsLoading(false));
   }, [
      clientId,
      commentId,
      closeModalHandler,
      setComments,
      setIsLoading,
      setItemsTotalCount,
      setErrorInfo,
      t,
   ]);

   const renderTitle = useCallback(() => {
      switch (action) {
         case 'ADD':
            return t('modals.comment.add_title');
         case 'EDIT':
            return t('modals.comment.edit_title');
         case 'DELETE':
            return t('modals.comment.delete_title');
      }
   }, [action, t]);

   const handleActionClick = useCallback(() => {
      switch (action) {
         case 'ADD':
            postComment();
            break;
         case 'EDIT':
            editComment();
            break;
         case 'DELETE':
            deleteComment();
            break;
      }
   }, [editComment, postComment, deleteComment, action]);

   return (
      <CustomModal
         {...props}
         {...(action === 'DELETE'
            ? {
                 icon: 'TRASH',
                 innerTitle: t('modals.comment.delete_title'),
                 onClose: closeModalHandler,
              }
            : {
                 title: renderTitle(),
                 onClose: handleShowCloseWarning,
                 closeWarningModalProps: {
                    opened: showCloseWarning,
                    onClose: handleHideCloseWarning,
                    onConfirm: closeModalHandler,
                 },
              })}
         error={errorInfo}
         isLoading={isLoading}
         primaryButtonProps={{
            text: renderTitle(),
            variant: action === 'DELETE' ? 'filled-danger' : 'filled-primary',
            onClick: handleActionClick,
         }}
         secondaryButtonProps={{
            text: t('common.cancel'),
            variant: action === 'DELETE' ? 'outlined-danger' : 'text',
            onClick: action === 'DELETE' ? closeModalHandler : handleShowCloseWarning,
         }}
      >
         <div>
            {action === 'DELETE' ? (
               <div className={styles.deleteModalContent}>
                  <p>{t('modals.comment.confirm_delete')}</p>
                  <strong>{t('cancel_pallet_report_modal.irreversible')}</strong>
               </div>
            ) : (
               <>
                  <p className={styles.text}>
                     <Trans
                        i18nKey="modals.comment.info_not_visible"
                        components={{ strong: <strong /> }}
                     />
                  </p>
                  <StyledTextArea
                     value={commentText}
                     onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setCommentText(e.target.value)
                     }
                     label={t('modals.comment.content_label')}
                     placeholder={t('modals.comment.content_placeholder')}
                  />
               </>
            )}
         </div>
      </CustomModal>
   );
};

export default ClientDetailsCommentModal;
